import { useTheme } from '../ThemeContext';
import { useEffect, useState, useRef } from 'react';

export default function TechArchitecture() {
  const { dark } = useTheme();
  const [inView, setInView] = useState({});
  const [animStep, setAnimStep] = useState(0);
  const refs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if(e.isIntersecting) setInView(p=>({...p,[e.target.dataset.a]:true})); });
    }, {threshold:0.1});
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Auto-animate the data flow
  useEffect(() => {
    const t = setInterval(() => setAnimStep(s => (s+1) % 7), 900);
    return () => clearInterval(t);
  }, []);

  const bg = dark?'#060c18':'#f4f8ff';
  const border = dark?'rgba(0,212,255,0.13)':'rgba(0,120,200,0.13)';
  const accent = dark?'#00d4ff':'#0078c8';
  const text1 = dark?'#e8f4ff':'#0a1e3d';
  const text2 = dark?'#8ab4d4':'#3a5a7a';
  const text3 = dark?'#4a7090':'#7090b0';
  const gridC = dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.05)';
  const cardBg = dark?'#0d1929':'#ffffff';
  const cardShad = dark?'none':'0 2px 16px rgba(0,100,200,0.07)';
  const boxFill = dark?'#0d1929':'#f8fbff';
  const boxStroke = dark?'rgba(0,212,255,0.35)':'rgba(0,120,200,0.35)';
  const ref = k => el => { refs.current[k]=el; };

  // highlight color per animation step
  const hl = (step) => animStep === step ? accent : (dark?'rgba(0,212,255,0.25)':'rgba(0,120,200,0.25)');
  const hlFill = (step) => animStep === step ? (dark?'rgba(0,212,255,0.12)':'rgba(0,120,200,0.1)') : 'transparent';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .ta-root{background:${bg};color:${text1};font-family:'Inter',sans-serif;min-height:100vh;padding-top:60px;transition:background 0.35s;}
        .ta-root::before{content:'';position:fixed;inset:0;background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);background-size:52px 52px;pointer-events:none;z-index:0;}
        .fade-in{opacity:0;transform:translateY(22px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .fade-in.vis{opacity:1;transform:none;}
        .slabel{font-size:11px;font-weight:700;letter-spacing:3px;color:${text3};text-transform:uppercase;margin-bottom:16px;display:flex;align-items:center;gap:12px;}
        .slabel::after{content:'';flex:1;height:1px;background:${border};}
        .stitle{font-family:'Orbitron',monospace;font-size:clamp(20px,3vw,30px);font-weight:700;color:${text1};margin-bottom:10px;}
        .sbody{font-size:15px;color:${text2};line-height:1.8;margin-bottom:32px;max-width:660px;}
        @keyframes flowPulse{0%,100%{opacity:0.3;}50%{opacity:1;}}
        @keyframes dataDot{0%{transform:translateX(0);opacity:1;}100%{transform:translateX(60px);opacity:0;}}
      `}</style>

      <div className="ta-root">
        {/* hero */}
        <div style={{position:'relative',zIndex:1,padding:'60px 24px 40px',textAlign:'center',borderBottom:`1px solid ${border}`,background:dark?'rgba(0,212,255,0.02)':'rgba(0,120,200,0.02)'}}>
          <div style={{background:dark?'rgba(0,212,255,0.07)':'rgba(0,120,200,0.07)',border:`1px solid ${border}`,display:'inline-block',padding:'5px 18px',borderRadius:20,marginBottom:20}}>
            <span style={{fontSize:11,fontWeight:600,letterSpacing:3,color:accent,textTransform:'uppercase'}}> Technical Architecture</span>
          </div>
          <h1 style={{fontFamily:"'Orbitron',monospace",fontSize:'clamp(24px,5vw,48px)',fontWeight:900,color:text1,marginBottom:12,lineHeight:1.08}}>
            System Architecture<br/><span style={{color:accent}}>Hybrid LSTM Pipeline</span>
          </h1>
          <p style={{fontSize:16,color:text2,maxWidth:540,margin:'0 auto',lineHeight:1.7}}>
            End-to-end technical breakdown from 5G packet capture to ONNX-powered API predictions.
          </p>
        </div>

        <div style={{position:'relative',zIndex:1,maxWidth:1060,margin:'0 auto',padding:'60px 24px 80px'}}>

          {/* Main architecture SVG diagram */}
          <div data-a="diag" ref={ref('diag')}>
            <div className={`fade-in${inView.diag?' vis':''}`}>
              <div className="slabel"> 01 · Full System Architecture</div>
              <div className="stitle">End-to-End Pipeline</div>
              <div style={{border:`1px solid ${border}`,background:cardBg,borderRadius:8,padding:24,boxShadow:cardShad,marginBottom:48,overflow:'auto'}}>
                <svg viewBox="0 0 900 520" width="100%" style={{fontFamily:'Inter, sans-serif',minWidth:640}}>
                  <defs>
                    <marker id="arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                      <path d="M1 1L8 5L1 9" fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </marker>
                    <marker id="arr2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                      <path d="M1 1L8 5L1 9" fill="none" stroke="#00cc66" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </marker>
                  </defs>

                  {/* ── ROW 1: DATA SOURCES ── */}
                  <text x="450" y="28" textAnchor="middle" fontSize="10" fill={text3} letterSpacing="3" fontWeight="700">DATA LAYER</text>

                  {/* KTH ExPECA */}
                  <rect x="20" y="44" width="140" height="64" rx="6" fill={hlFill(0)} stroke={hl(0)} strokeWidth="1.5"/>
                  <text x="90" y="70" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>KTH ExPECA</text>
                  <text x="90" y="86" textAnchor="middle" fontSize="10" fill={text3}>Testbed 5G Network</text>
                  <text x="90" y="100" textAnchor="middle" fontSize="10" fill={accent}>40,000 traces</text>

                  {/* TR1.csv */}
                  <rect x="180" y="44" width="110" height="64" rx="6" fill={hlFill(0)} stroke={hl(0)} strokeWidth="1.5"/>
                  <text x="235" y="70" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>TR1.csv</text>
                  <text x="235" y="86" textAnchor="middle" fontSize="10" fill={text3}>Training data</text>
                  <text x="235" y="100" textAnchor="middle" fontSize="10" fill={accent}>IP + SN columns</text>

                  {/* TE1.csv */}
                  <rect x="310" y="44" width="110" height="64" rx="6" fill={hlFill(0)} stroke={hl(0)} strokeWidth="1.5"/>
                  <text x="365" y="70" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>TE1.csv</text>
                  <text x="365" y="86" textAnchor="middle" fontSize="10" fill={text3}>Test data</text>
                  <text x="365" y="100" textAnchor="middle" fontSize="10" fill={accent}>Held-out eval</text>

                  {/* User CSV */}
                  <rect x="440" y="44" width="110" height="64" rx="6" fill={hlFill(0)} stroke={hl(0)} strokeWidth="1.5"/>
                  <text x="495" y="70" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>User CSV</text>
                  <text x="495" y="86" textAnchor="middle" fontSize="10" fill={text3}>Any 5G trace</text>
                  <text x="495" y="100" textAnchor="middle" fontSize="10" fill={accent}>Min 30 rows</text>

                  {/* ── ROW 2: PREPROCESSING ── */}
                  <text x="450" y="154" textAnchor="middle" fontSize="10" fill={text3} letterSpacing="3" fontWeight="700">PREPROCESSING</text>

                  <rect x="130" y="168" width="200" height="64" rx="6" fill={hlFill(1)} stroke={hl(1)} strokeWidth="1.5"/>
                  <text x="230" y="194" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Min-Max Normalization</text>
                  <text x="230" y="210" textAnchor="middle" fontSize="10" fill={text3}>Scale to [0,1] using train stats</text>
                  <text x="230" y="224" textAnchor="middle" fontSize="10" fill={accent}>min=16.18ms  max=21.87ms</text>

                  <rect x="360" y="168" width="200" height="64" rx="6" fill={hlFill(1)} stroke={hl(1)} strokeWidth="1.5"/>
                  <text x="460" y="194" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Sequence Builder</text>
                  <text x="460" y="210" textAnchor="middle" fontSize="10" fill={text3}>Sliding window extraction</text>
                  <text x="460" y="224" textAnchor="middle" fontSize="10" fill={accent}>Window = 30 timesteps</text>

                  {/* ── ROW 3: MODEL ── */}
                  <text x="450" y="284" textAnchor="middle" fontSize="10" fill={text3} letterSpacing="3" fontWeight="700">MODEL LAYER</text>

                  <rect x="80" y="298" width="740" height="80" rx="8" fill={hlFill(2)} stroke={hl(2)} strokeWidth="2"/>
                  <text x="450" y="324" textAnchor="middle" fontSize="13" fontWeight="700" fill={text1}>Hybrid Block-Wise Recursive LSTM</text>

                  {/* LSTM internals */}
                  {[['Input\nLayer',120,344],['LSTM\n50 units',240,344],['Dense(1)',370,344],['×10 Steps',490,344],['Reset\nReal Data',620,344],['Output\nBlock',740,344]].map(([lbl,x,y],i)=>(
                    <g key={lbl}>
                      <rect x={x-42} y={y-18} width="84" height="36" rx="4"
                        fill={animStep===i+2?accent+'22':'transparent'}
                        stroke={animStep===i+2?accent:boxStroke}
                        strokeWidth={animStep===i+2?1.5:1}/>
                      {lbl.split('\n').map((line,j)=>(
                        <text key={j} x={x} y={y+(j-0.5)*14} textAnchor="middle" fontSize="10" fontWeight="600"
                          fill={animStep===i+2?accent:text2}>{line}</text>
                      ))}
                      {i < 5 && <line x1={x+42} y1={y} x2={x+58} y2={y} stroke={accent} strokeWidth="1" markerEnd="url(#arr)"/>}
                    </g>
                  ))}

                  {/* ── ROW 4: ONNX / API ── */}
                  <text x="450" y="436" textAnchor="middle" fontSize="10" fill={text3} letterSpacing="3" fontWeight="700">DEPLOYMENT LAYER</text>

                  <rect x="80" y="450" width="160" height="56" rx="6" fill={hlFill(4)} stroke={hl(4)} strokeWidth="1.5"/>
                  <text x="160" y="474" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>ONNX Runtime</text>
                  <text x="160" y="490" textAnchor="middle" fontSize="10" fill={text3}>80MB · 3× faster</text>

                  <rect x="270" y="450" width="160" height="56" rx="6" fill={hlFill(5)} stroke={hl(5)} strokeWidth="1.5"/>
                  <text x="350" y="474" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Flask REST API</text>
                  <text x="350" y="490" textAnchor="middle" fontSize="10" fill={text3}>Gunicorn · Render</text>

                  <rect x="460" y="450" width="160" height="56" rx="6" fill={hlFill(5)} stroke={hl(5)} strokeWidth="1.5"/>
                  <text x="540" y="474" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>React Frontend</text>
                  <text x="540" y="490" textAnchor="middle" fontSize="10" fill={text3}>Recharts · jsPDF · Vercel</text>

                  <rect x="660" y="450" width="160" height="56" rx="6" fill="rgba(0,204,102,0.1)" stroke="#00cc66" strokeWidth="1.5"/>
                  <text x="740" y="474" textAnchor="middle" fontSize="11" fontWeight="700" fill="#00cc66">PDF Report</text>
                  <text x="740" y="490" textAnchor="middle" fontSize="10" fill={text3}>Charts · Metrics · Table</text>

                  {/* Connecting arrows between rows */}
                  {/* Data → Preprocessing */}
                  <line x1="90" y1="108" x2="200" y2="168" stroke={accent} strokeWidth="1" markerEnd="url(#arr)" strokeDasharray="4 3" opacity="0.5"/>
                  <line x1="235" y1="108" x2="235" y2="168" stroke={accent} strokeWidth="1" markerEnd="url(#arr)" strokeDasharray="4 3" opacity="0.5"/>
                  <line x1="365" y1="108" x2="415" y2="168" stroke={accent} strokeWidth="1" markerEnd="url(#arr)" strokeDasharray="4 3" opacity="0.5"/>
                  <line x1="495" y1="108" x2="450" y2="168" stroke={accent} strokeWidth="1" markerEnd="url(#arr)" strokeDasharray="4 3" opacity="0.5"/>
                  {/* Preprocessing → Model */}
                  <line x1="300" y1="232" x2="300" y2="298" stroke={accent} strokeWidth="1" markerEnd="url(#arr)" strokeDasharray="4 3" opacity="0.5"/>
                  <line x1="450" y1="232" x2="450" y2="298" stroke={accent} strokeWidth="1" markerEnd="url(#arr)" strokeDasharray="4 3" opacity="0.5"/>
                  {/* Model → ONNX */}
                  <line x1="200" y1="378" x2="180" y2="450" stroke="#00cc66" strokeWidth="1.5" markerEnd="url(#arr2)"/>
                  <line x1="370" y1="378" x2="360" y2="450" stroke="#00cc66" strokeWidth="1.5" markerEnd="url(#arr2)"/>
                  {/* ONNX → Flask → React → PDF */}
                  <line x1="240" y1="478" x2="270" y2="478" stroke="#00cc66" strokeWidth="1.5" markerEnd="url(#arr2)"/>
                  <line x1="430" y1="478" x2="460" y2="478" stroke="#00cc66" strokeWidth="1.5" markerEnd="url(#arr2)"/>
                  <line x1="620" y1="478" x2="660" y2="478" stroke="#00cc66" strokeWidth="1.5" markerEnd="url(#arr2)"/>
                </svg>
              </div>
            </div>
          </div>

          {/* LSTM internals detail */}
          <div data-a="lstm" ref={ref('lstm')}>
            <div className={`fade-in${inView.lstm?' vis':''}`}>
              <div className="slabel"> 02 · LSTM Cell Architecture</div>
              <div className="stitle">Memory Cell Detail</div>
              <div style={{border:`1px solid ${border}`,background:cardBg,borderRadius:8,padding:24,boxShadow:cardShad,marginBottom:48}}>
                <svg viewBox="0 0 860 280" width="100%" style={{fontFamily:'Inter, sans-serif',minWidth:500}}>
                  <defs>
                    <marker id="arrL" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                      <path d="M1 1L8 5L1 9" fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </marker>
                  </defs>
                  {/* Input */}
                  <rect x="20" y="110" width="100" height="60" rx="6" fill={boxFill} stroke={boxStroke} strokeWidth="1.5"/>
                  <text x="70" y="136" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Input</text>
                  <text x="70" y="152" textAnchor="middle" fontSize="10" fill={accent}>x_t</text>
                  <text x="70" y="164" textAnchor="middle" fontSize="9" fill={text3}>shape(1,30,1)</text>

                  {/* Forget gate */}
                  <rect x="160" y="40" width="120" height="52" rx="6" fill={boxFill} stroke="#ff5577" strokeWidth="1.5"/>
                  <text x="220" y="62" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Forget Gate</text>
                  <text x="220" y="78" textAnchor="middle" fontSize="10" fill="#ff5577">σ(W_f · [h,x] + b_f)</text>

                  {/* Input gate */}
                  <rect x="160" y="114" width="120" height="52" rx="6" fill={boxFill} stroke="#f0a500" strokeWidth="1.5"/>
                  <text x="220" y="136" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Input Gate</text>
                  <text x="220" y="152" textAnchor="middle" fontSize="10" fill="#f0a500">σ(W_i · [h,x] + b_i)</text>

                  {/* Cell state */}
                  <rect x="160" y="188" width="120" height="52" rx="6" fill={boxFill} stroke="#9966ff" strokeWidth="1.5"/>
                  <text x="220" y="210" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Cell State</text>
                  <text x="220" y="226" textAnchor="middle" fontSize="10" fill="#9966ff">tanh(W_c · [h,x] + b_c)</text>

                  {/* LSTM core */}
                  <rect x="330" y="80" width="160" height="120" rx="8" fill={dark?'rgba(0,212,255,0.06)':'rgba(0,120,200,0.05)'} stroke={accent} strokeWidth="2"/>
                  <text x="410" y="106" textAnchor="middle" fontSize="12" fontWeight="700" fill={text1}>LSTM Cell</text>
                  <text x="410" y="124" textAnchor="middle" fontSize="10" fill={accent}>50 units</text>
                  <text x="410" y="142" textAnchor="middle" fontSize="10" fill={text3}>h_t-1 · c_t-1</text>
                  <text x="410" y="158" textAnchor="middle" fontSize="10" fill={text3}>↓  hidden state</text>
                  <text x="410" y="174" textAnchor="middle" fontSize="10" fill={accent}>h_t → next step</text>

                  {/* Output gate */}
                  <rect x="540" y="80" width="120" height="52" rx="6" fill={boxFill} stroke="#00cc66" strokeWidth="1.5"/>
                  <text x="600" y="102" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Output Gate</text>
                  <text x="600" y="118" textAnchor="middle" fontSize="10" fill="#00cc66">σ(W_o · [h,x] + b_o)</text>

                  {/* Dense output */}
                  <rect x="540" y="154" width="120" height="52" rx="6" fill={boxFill} stroke={accent} strokeWidth="1.5"/>
                  <text x="600" y="176" textAnchor="middle" fontSize="11" fontWeight="700" fill={text1}>Dense(1)</text>
                  <text x="600" y="192" textAnchor="middle" fontSize="10" fill={accent}>Linear activation</text>

                  {/* Final prediction */}
                  <rect x="720" y="114" width="120" height="52" rx="6" fill="rgba(0,204,102,0.1)" stroke="#00cc66" strokeWidth="2"/>
                  <text x="780" y="136" textAnchor="middle" fontSize="11" fontWeight="700" fill="#00cc66">Predicted</text>
                  <text x="780" y="152" textAnchor="middle" fontSize="10" fill="#00cc66">Delay (ms)</text>

                  {/* Arrows */}
                  <line x1="120" y1="140" x2="160" y2="140" stroke={accent} strokeWidth="1.2" markerEnd="url(#arrL)"/>
                  <line x1="280" y1="66" x2="330" y2="120" stroke="#ff5577" strokeWidth="1" markerEnd="url(#arrL)"/>
                  <line x1="280" y1="140" x2="330" y2="140" stroke="#f0a500" strokeWidth="1" markerEnd="url(#arrL)"/>
                  <line x1="280" y1="214" x2="330" y2="160" stroke="#9966ff" strokeWidth="1" markerEnd="url(#arrL)"/>
                  <line x1="490" y1="120" x2="540" y2="106" stroke="#00cc66" strokeWidth="1.2" markerEnd="url(#arrL)"/>
                  <line x1="490" y1="160" x2="540" y2="180" stroke={accent} strokeWidth="1.2" markerEnd="url(#arrL)"/>
                  <line x1="660" y1="180" x2="720" y2="140" stroke="#00cc66" strokeWidth="1.5" markerEnd="url(#arrL)"/>

                  {/* Recurrent feedback arrow */}
                  <path d="M 410 200 Q 410 250 200 250 Q 90 250 90 200" fill="none" stroke={dark?'rgba(0,212,255,0.3)':'rgba(0,120,200,0.3)'} strokeWidth="1" strokeDasharray="5 3" markerEnd="url(#arrL)"/>
                  <text x="260" y="265" textAnchor="middle" fontSize="9" fill={text3}>h_t → h_t-1 (recurrent)</text>
                </svg>
              </div>
            </div>
          </div>

          {/* Hybrid algorithm */}
          <div data-a="algo" ref={ref('algo')}>
            <div className={`fade-in${inView.algo?' vis':''}`}>
              <div className="slabel"> 03 · Hybrid Block Algorithm</div>
              <div className="stitle">Block-Wise Recursive Logic</div>
              <div style={{border:`1px solid ${border}`,background:dark?'#000d1a':'#f6f9ff',borderRadius:8,padding:24,fontFamily:'monospace',fontSize:13,color:text1,lineHeight:2,boxShadow:cardShad,marginBottom:48}}>
                <div style={{color:text3,marginBottom:8}}> Hybrid Block-Wise Recursive Prediction</div>
                <div><span style={{color:'#ff5577'}}>function</span> <span style={{color:accent}}>predictHybrid</span>(normalizedValues):</div>
                <div style={{paddingLeft:20}}>i = 0</div>
                <div style={{paddingLeft:20}}><span style={{color:'#f0a500'}}>while</span> (i + SEQ_LEN + FUTURE_STEPS &lt;= len(values)):</div>
                <div style={{paddingLeft:40,color:text3}}> Get 30 real observations as context</div>
                <div style={{paddingLeft:40}}>context = values[i : i + <span style={{color:accent}}>30</span>]</div>
                <div style={{paddingLeft:40,color:text3}}> Predict 10 future steps recursively</div>
                <div style={{paddingLeft:40}}><span style={{color:'#f0a500'}}>for</span> step in range(<span style={{color:accent}}>10</span>):</div>
                <div style={{paddingLeft:60}}>prediction = LSTM.predict(context)</div>
                <div style={{paddingLeft:60}}>context = context[<span style={{color:accent}}>1:</span>] + [prediction]</div>
                <div style={{paddingLeft:40,color:text3}}> KEY: advance with REAL data, not predictions</div>
                <div style={{paddingLeft:40}}>i += <span style={{color:'#00cc66'}}>10  // reset drift every block</span></div>
                <div style={{paddingLeft:20,color:text3}}> Error bounded to O(10), never accumulates</div>
                <div style={{paddingLeft:20}}><span style={{color:'#ff5577'}}>return</span> denormalize(predictions)</div>
              </div>
            </div>
          </div>

          {/* Stack summary */}
          <div data-a="stack" ref={ref('stack')}>
            <div className={`fade-in${inView.stack?' vis':''}`}>
              <div className="slabel"> 04 · Deployment Stack</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16}}>
                {[
                  {title:'Model Training',items:['TensorFlow 2.x / Keras','LSTM(50) + Dense(1)','Adam optimizer','MSE loss · 20 epochs'],color:'#9966ff'},
                  {title:'ONNX Conversion',items:['model.export() → SavedModel','python -m tf2onnx.convert','--opset 13','5.6× RAM reduction'],color:'#f0a500'},
                  {title:'Backend API',items:['Flask + Flask-CORS','ONNX Runtime inference','Gunicorn (1 worker)','Render free tier'],color:accent},
                  {title:'Frontend',items:['React 18 + Recharts','jsPDF reports','React Dropzone','Vercel CDN'],color:'#00cc66'},
                ].map(s=>(
                  <div key={s.title} style={{border:`1px solid ${s.color}33`,background:cardBg,borderRadius:8,padding:20,boxShadow:cardShad,transition:'all 0.25s'}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=s.color+'66';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=s.color+'33';}}
                  >
                    <div style={{position:'relative'}}>
                      <div style={{position:'absolute',top:0,left:-20,right:-20,height:2,background:s.color,borderRadius:'8px 8px 0 0'}}/>
                    </div>
                    <div style={{fontFamily:"'Orbitron',monospace",fontSize:12,fontWeight:700,color:s.color,marginBottom:14,marginTop:8}}>{s.title}</div>
                    {s.items.map(item=>(
                      <div key={item} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:text2,marginBottom:6}}>
                        <div style={{width:4,height:4,borderRadius:'50%',background:s.color,flexShrink:0}}/>
                        {item}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
